import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Icon } from '../Icon.tsx';
import { SheetShell } from './SheetShell.tsx';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  tagline?: string;
  shareTitle?: string;
  shareText?: string;
  /** Pulls the URL when the sheet opens. Re-invoked each open. */
  generate: () => Promise<{ url: string; expires_at?: number }>;
  /** Optional caption beneath the link, e.g. "Single use · expires in 7 days" */
  caption?: string | ((info: { expires_at?: number }) => string);
};

export function ShareLinkSheet({
  open,
  onClose,
  title,
  tagline,
  shareTitle,
  shareText,
  generate,
  caption,
}: Props) {
  const [link, setLink] = useState<{ url: string; expires_at?: number } | null>(null);
  const [qrSvg, setQrSvg] = useState<string>('');
  const [err, setErr] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    setLink(null);
    setQrSvg('');
    setErr('');
    generate()
      .then(async (l) => {
        setLink(l);
        const svg = await QRCode.toString(l.url, {
          type: 'svg',
          errorCorrectionLevel: 'M',
          margin: 1,
          color: { dark: '#2a221b', light: '#fbf6ed' },
          width: 240,
        });
        setQrSvg(svg);
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : 'Failed'));
    // generate intentionally not in deps; we re-run on open transitions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const captionText =
    typeof caption === 'function' ? caption({ expires_at: link?.expires_at }) : caption;

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={title}
      tagline={tagline}
      accent="accent"
      icon={(p) => <Icon.share {...p} />}
      primaryLabel="Done"
      secondaryLabel="Close"
      onPrimary={onClose}
    >
      {err && <div style={{ color: 'var(--danger)', fontSize: 13, padding: 12 }}>{err}</div>}
      {!err && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <div
            style={{
              width: 260,
              height: 260,
              borderRadius: 18,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              display: 'grid',
              placeItems: 'center',
              padding: 10,
            }}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted SVG from qrcode lib
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />
          {link && (
            <>
              <div
                className="lt-mono"
                style={{
                  fontSize: 11,
                  color: 'var(--text-3)',
                  textAlign: 'center',
                  wordBreak: 'break-all',
                  padding: '0 16px',
                }}
              >
                {link.url}
              </div>
              <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                <button
                  type="button"
                  className="lt-btn lt-btn-soft"
                  style={{ flex: 1, padding: '11px 0', fontSize: 13 }}
                  onClick={async () => {
                    if (!navigator.clipboard) return;
                    await navigator.clipboard.writeText(link.url);
                    toast.success('Link copied');
                  }}
                >
                  Copy
                </button>
                {navigator.share && (
                  <button
                    type="button"
                    className="lt-btn lt-btn-primary"
                    style={{ flex: 1, padding: '11px 0', fontSize: 13 }}
                    onClick={() =>
                      navigator
                        .share({
                          title: shareTitle ?? title,
                          text: shareText,
                          url: link.url,
                        })
                        .catch(() => {})
                    }
                  >
                    Share
                  </button>
                )}
              </div>
              {captionText && (
                <div style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'center' }}>
                  {captionText}
                </div>
              )}
            </>
          )}
          {!link && !err && (
            <div style={{ color: 'var(--text-3)', fontSize: 13, padding: 12 }}>Generating…</div>
          )}
        </div>
      )}
    </SheetShell>
  );
}
